/**
 * Fleet Status Service
 *
 * Fetches real-time GPU fleet health metrics from the Fleet Status API endpoint.
 * Provides aggregated metrics including GPU utilization, queue depth,
 * estimated wait time, and job processing rate.
 *
 * Requirements: 1.4, 7.1
 */

import { AuthenticatedApiClient } from './authenticated-api-client';

/**
 * Fleet health metrics returned by the GET /fleet-status endpoint.
 */
export interface FleetStatusMetrics {
    /** GPU utilization as a percentage (0.0–100.0), one decimal place */
    gpu_utilization_percent: number;
    /** Average queued jobs per active instance, one decimal place */
    queue_depth: number;
    /** Estimated wait time for a new job in seconds (non-negative integer) */
    estimated_wait_seconds: number;
    /** Job completion rate as a percentage (completed/total * 100), one decimal place */
    job_completion_rate: number;
    /** Number of active (ready + idle + processing) instances */
    active_instance_count: number;
    /** ISO-8601 UTC timestamp of when metrics were last computed */
    last_updated: string;
}

/**
 * Fleet Status Service class for fetching fleet health metrics.
 */
export class FleetStatusService {
    private client: AuthenticatedApiClient;

    constructor() {
        this.client = new AuthenticatedApiClient();
    }

    /**
     * Fetches current fleet status metrics from the API.
     *
     * @returns Promise resolving to fleet health metrics
     * @throws Error if the API request fails
     *
     * @example
     * ```typescript
     * const service = new FleetStatusService();
     * const metrics = await service.getFleetStatus();
     * console.log(metrics.gpu_utilization_percent); // 66.7
     * console.log(metrics.estimated_wait_seconds); // 180
     * ```
     */
    async getFleetStatus(): Promise<FleetStatusMetrics> {
        try {
            const response = await this.client.apiRequest('/fleet-status', { method: 'GET' });
            const data = await response.json();
            return data as FleetStatusMetrics;
        } catch (error) {
            console.error('Error fetching fleet status:', error);
            throw new Error(
                `Failed to fetch fleet status: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }
}
